import { Component, inject, signal } from '@angular/core';
import { ConfiguredActionDetailDto, ConfiguredActionMergeDto, ConfiguredActionService } from '@artemis-software/wr-api';
import { createForm } from '@sonofabit/ng-core';
import { Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { NbComponentStatus, NbDialogService, NbToastrService } from '@nebular/theme';
import { DeleteDialogComponent } from '@components/dialog/delete-dialog/delete-dialog.component';
import { catalogTenantId, currentTenantId } from '@/utils/admin-utils';
import { loader } from '@/utils/signals/loader';
import { tryWithLoadingSignal } from '@/utils/async-utils';
import { PendingChanges } from '@guards/pending-changes.guard';
import SeverityEnum = ConfiguredActionDetailDto.SeverityEnum;

export interface SeverityOption {
  value: SeverityEnum;
  label: string;
  status: NbComponentStatus;
}

export const SEVERITY_OPTIONS: SeverityOption[] = [
  { value: SeverityEnum.ImminentDanger, label: 'Gefahr', status: 'danger' },
  { value: SeverityEnum.Damage, label: 'Schaden', status: 'warning' },
  { value: SeverityEnum.Recommendation, label: 'Empfehlung', status: 'info' },
  { value: SeverityEnum.NoAction, label: 'Keine Maßnahme', status: 'success' },
];

const createActionForm = (action?: ConfiguredActionDetailDto) => {
  return createForm(({ field }) => ({
    id: field({ value: action?.id }),
    name: field({ value: action?.name ?? '', validators: [Validators.required] }),
    severity: field({ value: action?.severity ?? SeverityEnum.Recommendation, validators: [Validators.required] }),
  }));
};

type ActionFormType = ReturnType<typeof createActionForm>;

@Component({
  standalone: false,
  selector: 'wr-configured-actions-page',
  templateUrl: './configured-actions-page.component.html',
  styleUrls: ['./configured-actions-page.component.scss'],
})
export class ConfiguredActionsPageComponent implements PendingChanges {
  private readonly configuredActionService = inject(ConfiguredActionService);
  private readonly dialogService = inject(NbDialogService);
  private readonly toastrService = inject(NbToastrService);
  private readonly currentTenantId = currentTenantId();
  private readonly catalogTenantId = catalogTenantId();

  readonly severityOptions = SEVERITY_OPTIONS;

  readonly loading = loader();
  readonly saving = loader();
  readonly isEditing = signal(false);
  readonly canEdit = signal(false);
  readonly actionForms = signal<ActionFormType[]>([]);
  private readonly toDeleteIds = signal<string[]>([]);

  constructor() {
    void this.load();
  }

  private async load(): Promise<void> {
    await tryWithLoadingSignal(this.loading, async () => {
      const actions = await firstValueFrom(this.configuredActionService.findAllDetailDtos({
        tenantId: this.catalogTenantId(),
      }));
      const sorted = [...actions].sort((a, b) => a.ranking - b.ranking);
      this.canEdit.set(sorted.length === 0 || sorted[0].permissions.canEdit);
      this.actionForms.set(sorted.map((action) => createActionForm(action)));
      this.toDeleteIds.set([]);
      this.setFormsEnabled(false);
    });
  }

  hasPendingChanges(): boolean {
    return this.isEditing() && (this.actionForms().some((form) => form.dirty) || this.toDeleteIds().length > 0);
  }

  severityStatus(form: ActionFormType): NbComponentStatus {
    const severity = form.controls.severity.value;
    return this.severityOptions.find((option) => option.value === severity)?.status ?? 'basic';
  }

  onEdit(): void {
    this.isEditing.set(true);
    this.setFormsEnabled(true);
  }

  async cancel(): Promise<void> {
    this.isEditing.set(false);
    await this.load();
  }

  addAction(): void {
    const form = createActionForm();
    this.actionForms.update((forms) => [...forms, form]);
  }

  async removeAction(index: number): Promise<void> {
    const form = this.actionForms()[index];
    const id = form.controls.id.value;
    if (id) {
      const ref = this.dialogService.open(DeleteDialogComponent, {
        context: {
          title: 'Aktion löschen',
          message: 'Bist du sicher, dass du diese Aktion löschen willst? Bestehende Prüfzeilen behalten ihre Zuordnung.',
        },
      });
      const confirmed = await firstValueFrom(ref.onClose);
      if (!confirmed) return;
      this.toDeleteIds.update((ids) => [...ids, id]);
    }
    this.actionForms.update((forms) => forms.filter((_, formIndex) => formIndex !== index));
  }

  moveAction(index: number, delta: number): void {
    const target = index + delta;
    const forms = [...this.actionForms()];
    if (target < 0 || target >= forms.length) return;
    const [form] = forms.splice(index, 1);
    forms.splice(target, 0, form);
    forms.forEach((movedForm) => movedForm.markAsDirty());
    this.actionForms.set(forms);
  }

  async submit(): Promise<void> {
    const forms = this.actionForms();
    forms.forEach((form) => form.markAllAsTouched());
    if (forms.some((form) => !form.valid)) {
      this.toastrService.danger('Aktionen können nicht gespeichert werden, es gibt noch Fehler.', 'Aktionen');
      return;
    }
    await tryWithLoadingSignal(this.saving, async () => {
      await Promise.all(this.toDeleteIds().map((id) =>
        firstValueFrom(this.configuredActionService.deleteById(id))));
      this.toDeleteIds.set([]);

      const dtos: ConfiguredActionMergeDto[] = await Promise.all(forms.map(async (form, index) => ({
        ...(await form.getMappedValue()),
        ranking: index,
        tenantId: this.currentTenantId(),
      })));
      await firstValueFrom(this.configuredActionService.mergeAll(dtos));

      this.toastrService.success('Aktionen wurden gespeichert.', 'Aktionen');
      this.isEditing.set(false);
      await this.load();
    });
  }

  private setFormsEnabled(enabled: boolean): void {
    this.actionForms().forEach((form) => enabled ? form.enable() : form.disable());
  }
}
